import { enableProdMode, NgModuleRef, provideZoneChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { HostContextService, setShellPolarisUx } from './app/services/host-context/host-context.service';

// ─── Custom Element Tag ───────────────────────────────────────────────────────
const APP_TAG = 'user-login-contact-info';

// ─── Polaris Shell Detection ──────────────────────────────────────────────────
// DI is not available before bootstrap, but HostContextService has no dependencies,
// so instantiate it directly to reuse the single source of truth for host detection.
function isPolaris(): boolean {
  return new HostContextService().isPolaris;
}

// ─── Polaris Shell Lifecycle Exports ──────────────────────────────────────────
let appModuleRef: NgModuleRef<AppModule> | null = null;

export function bootstrap(): Promise<void> {
  if (!environment.production) console.log('[wpro] lifecycle: bootstrap');
  return Promise.resolve();
}

export function mount(props: any): Promise<void> {
  if (!environment.production) console.log('[wpro] lifecycle: mount', props);

  // Single source of truth for Polaris UX: the shell derives this from its feature flag and
  // passes it via props. Must be set before Angular bootstraps so HostContextService can read it.
  // props is `any`, so coerce explicitly — only a real `true` (or the string 'true') enables
  // Polaris UX; anything else (incl. the string 'false', which is truthy) stays off.
  const usePolarisUx = props?.usePolarisUx === true || props?.usePolarisUx === 'true';
  setShellPolarisUx(usePolarisUx);

  if (environment.production) enableProdMode();

  const container: HTMLElement =
    document.getElementById(props?.mountSlotElementId) ||
    document.body;

  let rootEl = container.querySelector(APP_TAG);
  if (!rootEl) {
    rootEl = document.createElement(APP_TAG);
    container.appendChild(rootEl);
  }

  if (!appModuleRef) {
    return platformBrowserDynamic().bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()] })
      .then((ref) => {
        appModuleRef = ref;
        if (!environment.production) console.log('[wpro] ✅ Angular bootstrapped successfully');
      })
      .catch((err) => {
        console.error('[wpro] Angular bootstrap failed:', err);
        throw err; // Re-throw so host (single-spa) sees the failure
      });
  }

  return Promise.resolve();
}

export function unmount(props: any): Promise<void> {
  if (!environment.production) console.log('[wpro] lifecycle: unmount', props);

  const container: HTMLElement =
    document.getElementById(props?.mountSlotElementId) ||
    document.body;

  const el = container.querySelector(APP_TAG);
  if (el) {
    el.remove();
  }

  if (appModuleRef) {
    appModuleRef.destroy();
    appModuleRef = null;
  }

  return Promise.resolve();
}

// Register lifecycle hooks on global registry (Angular build strips ESM exports)
(window as any).__MFE_LIFECYCLES__ = (window as any).__MFE_LIFECYCLES__ || {};
(window as any).__MFE_LIFECYCLES__['user-login-contact-info'] = { bootstrap, mount, unmount };

// ─── Standalone Bootstrap (dotcom mode) ───────────────────────────────────────
if (!isPolaris()) {
  if (environment.production) {
    enableProdMode();
  }
  platformBrowserDynamic()
    .bootstrapModule(AppModule, { applicationProviders: [provideZoneChangeDetection()] })
    .catch((err) => console.error(err));
} else {
  if (!environment.production) console.log('[wpro] running inside Polaris shell — lifecycle hooks will handle bootstrap');
}
